
import React, { useState } from 'react';
import { Tabs, Image, Spin } from 'antd';
import CommonModal from "PhishingManager/components/Modal";
import { ModalProps } from "PhishingManager/components/Modal/Modal.types";
import InteractionPreviewImg from "../images/interaction-preview.png";
import CalculationImg from "../images/calculation.png";
import EvaluationImg from "../images/evalution.png";
import ProcessImg from "../images/process-preview.png";
import DetectImg from "../images/detect.png";
import RatingDescImg from "../images/rating-desc.png";
import CheckListImg from "../images/checklist.png";
import { Icon } from 'Components';
import useDownloadPDFFromURL from 'Hooks/useDownloadFileFromURL';
import { AUTOMATION_PDF_URL, INTERACTION_PDF_URL } from '../CampaignReport.constant';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

const LearnMoreModal: React.FC<ModalProps> = ({
    open,
    onClose,
    onConfirm
}) => {
    const [activeTab, setActiveTab] = useState("interaction");

    const { downloadFile, loading } = useDownloadPDFFromURL();
    const { t } = useTranslation();

    return (
        <CommonModal
            visible={open}
            confirmText="Close"
            cancelText={null}
            iconType={null}
            onClose={onClose}
            onConfirm={onConfirm}
            modalTitle={"Learn More"}
            parentClass='learn-more-modal'>
            <Spin spinning={loading}>
                <div className='modal-body'>
                    <div className='tabs-section'>
                        <Tabs activeKey={activeTab} onChange={setActiveTab}>
                            <Tabs.TabPane tab={t('phishingManager.report.learnMore.interactionTitle')} key="interaction">
                                <InterpretResult t={t} />
                            </Tabs.TabPane>

                            <Tabs.TabPane tab={t('phishingManager.report.learnMore.automationTitle')} key="automation">
                                <AutomationDetect t={t} />
                            </Tabs.TabPane>
                        </Tabs>
                    </div>
                    <Icon type="download" className="icon-btn download-btn" onClick={() => downloadFile(activeTab === "interaction" ? INTERACTION_PDF_URL : AUTOMATION_PDF_URL, activeTab === "interaction" ? "interpreting_results" : "automation_detection")} />
                </div>
            </Spin>
        </CommonModal>
    );
};

export default LearnMoreModal;

const InterpretResult = ({ t }: { t: TFunction }) => {
    return <div className="content-section" id="interaction-content">
        <div>
            <p>{t('phishingManager.report.learnMore.interaction.introPurpose')}</p>
            <p>{t('phishingManager.report.learnMore.interaction.introProcess')}</p>
            <p>{t('phishingManager.report.learnMore.interaction.introResults')}</p>
        </div>

        <div>
            <h3>{t('phishingManager.report.learnMore.interaction.interactionsTitle')}</h3>
            <p>{t('phishingManager.report.learnMore.interaction.interactionsDesc1')}</p>
            <p>{t('phishingManager.report.learnMore.interaction.interactionsDesc2')}</p>

            <div className='result-desc'>
                <div className='passed'>
                    <span>{t('phishingManager.report.learnMore.interaction.passingTitle')}</span>
                    <p>
                        <strong>{t('phishingManager.report.learnMore.interaction.noInteractionLabel')} - </strong>
                        {t('phishingManager.report.learnMore.interaction.noInteractionDesc')}
                    </p>
                    <p>
                        <strong>{t('phishingManager.report.learnMore.interaction.openedEmailLabel')} - </strong>
                        {t('phishingManager.report.learnMore.interaction.openedEmailDesc')}
                    </p>
                </div>

                <div className='failed'>
                    <span>{t('phishingManager.report.learnMore.interaction.failingTitle')}</span>
                    <p>
                        <strong>{t('phishingManager.report.learnMore.interaction.clickedLinkLabel')} – </strong>
                        {t('phishingManager.report.learnMore.interaction.clickedLinkDesc')}
                    </p>
                    <p>
                        <strong>{t('phishingManager.report.learnMore.interaction.submittedDataLabel')} – </strong>
                        {t('phishingManager.report.learnMore.interaction.submittedDataDesc')}
                    </p>
                </div>
            </div>

            <Image preview={false} src={InteractionPreviewImg} alt="interaction-preview" />
        </div>

        <div>
            <h3>{t('phishingManager.report.learnMore.interaction.summaryTitle')}</h3>
            <p>{t('phishingManager.report.learnMore.interaction.summaryDesc')}</p>

            <ul>
                <li>
                    <strong>{t('phishingManager.report.learnMore.interaction.execSummaryLabel')} - </strong>
                    {t('phishingManager.report.learnMore.interaction.execSummaryDesc')}
                </li>
                <li>
                    <strong>{t('phishingManager.report.learnMore.interaction.susEmployeesLabel')} - </strong>
                    {t('phishingManager.report.learnMore.interaction.susEmployeesDesc')}
                </li>
                <li>
                    <strong>{t('phishingManager.report.learnMore.interaction.allResultsLabel')} - </strong>
                    {t('phishingManager.report.learnMore.interaction.allResultsDesc')}
                </li>
            </ul>
            <p>{t('phishingManager.report.learnMore.interaction.passRateDesc')}</p>
            <Image preview={false} src={CalculationImg} alt="calculation-preview" />
            <div className='note'>
                {t('phishingManager.report.learnMore.interaction.summaryNote')}
            </div>
        </div>

        <div>
            <h3>{t('phishingManager.report.learnMore.interaction.aidTitle')}</h3>
            <p>{t('phishingManager.report.learnMore.interaction.aidDesc')}</p>
            <p className="mt-12">
                {t('phishingManager.report.learnMore.interaction.aidImpact')}
            </p>

            <Image preview={false} src={ProcessImg} alt="process-preview" />
            <p>{t('phishingManager.report.learnMore.interaction.aidAlgorithm')}</p>
            <p>{t('phishingManager.report.learnMore.interaction.aidScope')}</p>
            <p>{t('phishingManager.report.learnMore.interaction.aidDetails')}</p>
            <p className="mt-12">
                {t('phishingManager.report.learnMore.interaction.aidScopeAlt')}
            </p>

            <Image preview={false} src={EvaluationImg} alt="evalution-preview" />
            <p>{t('phishingManager.report.learnMore.interaction.aidRating')}</p>
            <p>{t('phishingManager.report.learnMore.interaction.aidRecommendation')}</p>
        </div>

        <div>
            <h3>{t('phishingManager.report.learnMore.interaction.propertyTitle')}</h3>
            <p>{t('phishingManager.report.learnMore.interaction.propertyDesc')}</p>
            <p>{t('phishingManager.report.learnMore.interaction.propertyMetrics')}</p>
        </div>

        <div>
            <h3>{t('phishingManager.report.learnMore.interaction.advancedTitle')}</h3>
            <p>{t('phishingManager.report.learnMore.interaction.advancedDesc')}</p>
            <p>{t('phishingManager.report.learnMore.interaction.advancedDetails')}</p>
            <p>{t('phishingManager.report.learnMore.interaction.advancedNote')}</p>
        </div>

        <div>
            <h3>{t('phishingManager.report.learnMore.interaction.recommendationsTitle')}</h3>
            <p>{t('phishingManager.report.learnMore.interaction.trainingDesc')}</p>
            <p>{t('phishingManager.report.learnMore.interaction.libraryDesc')}</p>
            <p>{t('phishingManager.report.learnMore.interaction.resourcesDesc')}</p>
        </div>
    </div>
}

const AutomationDetect = ({ t } : { t: TFunction }) => {
    return (
        <div className="content-section" id="automation-content">
            <div>
                <p>{t('phishingManager.report.learnMore.automation.intro.p1')}</p>
                <p>{t('phishingManager.report.learnMore.automation.intro.p2')}</p>
                <p>{t('phishingManager.report.learnMore.automation.intro.p3')}</p>
            </div>

            <div>
                <h3>{t('phishingManager.report.learnMore.automation.howWorks.title')}</h3>
                <p className="mt-12">{t('phishingManager.report.learnMore.automation.howWorks.intro')}</p>
                <Image preview={false} src={DetectImg} alt="detect-preview" />
                <p>{t('phishingManager.report.learnMore.automation.howWorks.workflow')}</p>
                <p className="mt-12">{t('phishingManager.report.learnMore.automation.howWorks.rating')}</p>
                <Image preview={false} src={RatingDescImg} alt="rating-desc-preview" />
            </div>

            <div>
                <h3>{t('phishingManager.report.learnMore.automation.checks.title')}</h3>
                <p className="mt-12">{t('phishingManager.report.learnMore.automation.checks.intro')}</p>
                <Image preview={false} src={CheckListImg} alt="checklist-preview" />
                <div className="note">
                    {t('phishingManager.report.learnMore.automation.checks.note')}
                </div>
            </div>
        </div>
    );
};